/**
 * pages/components/Modal.js — Post detail and fact-checker editing bottom drawer
 * ================================================================================
 * Renders a bottom drawer (slides up from the bottom) for reading or editing a
 * fact-checked post.  Mirrors the Flutter app's showFactDetailSheet pattern.
 * Used by Newsfeed.js (the only consumer).
 *
 * Props
 * ------
 * isOpen   {boolean}  — controls drawer visibility
 * onClose  {function} — called when user cancels (no changes saved)
 * inpPost  {object}   — the post object passed in from the parent list item
 *                       (mutated in-place before onSubmit to avoid extra state)
 * onSubmit {function} — called when a FACT_CHECKER saves changes
 *
 * Role-based rendering
 * ---------------------
 * canEdit = FACT_CHECKER role AND post is not yet released (released=False).
 *
 * canEdit = true  → Header shows editable verdict Select; body shows AI fill
 *                   button, template button, Preview/Markdown toggle, Upload
 *                   panel, and TextArea editor; footer button reads "OK".
 * canEdit = false → Header shows read-only verdict Tag; body shows Markdown
 *                   render only; footer button reads "Got it" (matches Flutter).
 *
 * Edit lock
 * ---------
 * Once a post reaches the approval threshold the backend sets released=True.
 * Released posts are locked — no further edits are permitted by anyone.
 *
 * File upload flow (GCS pre-signed URLs)
 * ----------------------------------------
 * 1. handleUpload fires when Ant Design Upload calls customRequest.
 * 2. File is MD5-hashed via SparkMD5 to form a deterministic object name
 *    (images/<hash>.<ext> or videos/<hash>.<ext>) — prevents duplicate uploads.
 * 3. For videos, a thumbnail is extracted at t=1s and uploaded alongside.
 * 4. apiClient.sessionurl() returns { uploadUrl, uploadThumbUrl?, publicUrl, publicThumbUrl? }.
 * 5. Files are PUT directly to GCS via the pre-signed URL — the backend
 *    is never in the upload data path.
 * 6. The completed file entry is appended to fileList state.
 *
 * File removal
 * -------------
 * handleRemove queues the removed file in deleteList (not deleted yet).
 * On OK, apiClient.removeurl(deleteList) fires as a single batch call
 * so that a user who removes a file and then cancels does not lose it.
 *
 * handleOk — dirty check
 * -----------------------
 * Only calls onSubmit (→ backend update) when at least one of
 * lbl / exp / files has changed.  This prevents spurious writes when
 * a FACT_CHECKER opens a post and clicks OK without editing.
 *
 * API calls
 * ----------
 * apiClient.factCheck(addr)      — POST /session/fact_check  (AI gen-fill)
 * apiClient.sessionurl(name, ct) — POST /session/url         (pre-signed upload URL)
 * apiClient.removeurl(list)      — POST /session/remove_url  (batch GCS delete)
 */

import apiClient from "../../utils/apiClient";
import { Select, Input, Drawer, Divider, Button, message, Radio, Upload, Tag, Typography, Space } from "antd";
import { useUsr } from "../../App";
import { useState, useRef, useEffect } from "react";
import Markdown from "react-markdown";
import Marquee from "react-fast-marquee";
import { PlusOutlined, FormOutlined, ExportOutlined } from "@ant-design/icons";
import SparkMD5 from "spark-md5";
import "./markdown.css";

const { TextArea } = Input;
const { Text } = Typography;

// Ant Design tag colour map for post labels
const LBL_COLOR = { BLANK: "blue", REAL: "success", FAKE: "error", SPAM: "warning" };

// XHR-based PUT upload — allows onProgress callbacks that fetch() cannot provide
const xhrUpload = (url, body, contentType, onProgress) =>
    new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = e => { if (e.lengthComputable) onProgress(Math.round(e.loaded / e.total * 100)); };
        xhr.onload  = resolve;
        xhr.onerror = () => reject(new Error("Upload failed"));
        xhr.open("PUT", url);
        xhr.setRequestHeader("Content-Type", contentType);
        xhr.send(body);
    });

// Options for the verdict Select dropdown shown to FACT_CHECKERs
const lbls = [
    { value: "BLANK", label: "BLANK" },
    { value: "REAL", label: "REAL" },
    { value: "FAKE", label: "FAKE" },
    { value: "SPAM", label: "SPAM" },
];

/**
 * ModalContent — the single exported component.
 *
 * inpPost is mutated directly (inpPost.lbl = lbl, etc.) before calling
 * onSubmit so the parent list item updates instantly without a re-fetch.
 */
export const ModalContent = ({ isOpen, onClose, inpPost, onSubmit, onAck, onReact, likes = 0, dislikes = 0, severityCount = 0, userAffinity = null, userSeverity = null }) => {
    const { usr } = useUsr();

    // Local copies of the editable fields — only applied to inpPost on OK
    const [exp, setExp] = useState(inpPost.exp);
    const [lbl, setLbl] = useState(inpPost.lbl);
    const [messageApi, messageHolder] = message.useMessage();

    // FACT_CHECKERs start in Markdown edit mode; everyone else in Preview (read-only)
    const [view, setView] = useState(usr.role === "FACT_CHECKER" ? "Markdown" : "Preview");

    // fileList mirrors Ant Design Upload's internal list; starts from inpPost.files
    const [fileList, setFileList] = useState(inpPost.files);

    // Files removed during this session — batched and deleted on OK
    const [deleteList, setDeleteList] = useState([]);

    // Gemini AI fill loading state — drives the animated border around the TextArea
    const [isAiLoading, setIsAiLoading] = useState(false);

    // Local optimistic reaction counts for the interactive header row
    const [_likes,         set_likes]         = useState(likes);
    const [_dislikes,      set_dislikes]       = useState(dislikes);
    const [_severityCount, set_severityCount]  = useState(severityCount);
    const [_userAffinity,  set_userAffinity]   = useState(userAffinity);
    const [_userSeverity,  set_userSeverity]   = useState(userSeverity);

    const attrRef = useRef(null);
    const [needsMarquee, setNeedsMarquee] = useState(false);

    // After each render where attribution may have changed, measure whether the
    // static text overflows its container. If so, switch to a scrolling marquee.
    useEffect(() => {
        setNeedsMarquee(false);
        const id = requestAnimationFrame(() => {
            const el = attrRef.current;
            if (el) setNeedsMarquee(el.scrollWidth > el.offsetWidth);
        });
        return () => cancelAnimationFrame(id);
    }, [inpPost.by, inpPost.approvers?.join(',')]);

    const tapAffinity = (value) => {
        if (!onReact) return;
        const next = _userAffinity === value ? 0 : value;
        set_likes(l    => l + (next === 1  ? 1 : (_userAffinity === 1  ? -1 : 0)));
        set_dislikes(d => d + (next === -1 ? 1 : (_userAffinity === -1 ? -1 : 0)));
        set_userAffinity(next || null);
        onReact('affinity', next);
    };
    const tapSeverity = () => {
        if (!onReact) return;
        const next = _userSeverity === 1 ? 0 : 1;
        set_severityCount(s => s + (next === 1 ? 1 : -1));
        set_userSeverity(next || null);
        onReact('severity', next);
    };

    /**
     * gen_ai_fill — calls the AI fact-check endpoint and pre-fills the
     * explanation TextArea with the returned markdown.  The FACT_CHECKER
     * is expected to review and edit the generated content before saving.
     */
    const gen_ai_fill = () => {
        setIsAiLoading(true);
        apiClient.factCheck(inpPost.addr)
            .then(response => {
                setExp(response.data.gen_ai_fill);
                messageApi.success("Fact Checked the Claim.");
            })
            .catch(error => console.error("Error sending data:", error))
            .finally(() => setIsAiLoading(false));
    };

    /**
     * exp_fill_template — inserts a structured markdown template into the
     * explanation editor.  Provides headings for Claim Summary, Verdict,
     * Key Findings, and Sources to guide consistent fact-check write-ups.
     */
    const exp_fill_template = () => {
        setExp(`---

## 🧾 Claim Summary
A concise 1–2 sentence summary of the claim in neutral language.

## ✅ Verdict
Clearly state whether the claim is **True**, **False**, **Misleading**, **Partially True**, or **Uncertain**. Include a 1–2 sentence explanation.

## 🔍 Key Findings
Provide **10 bullet points** that support the verdict. Each point should be:
- Factual
- Concise
- Specific (include numbers, dates, names if relevant)
- Supported by high-quality sources (avoid weak blogs or opinion pieces)

## 🔗 Sources
Provide **3 to 5 clickable links** from credible sources (e.g. government websites, universities, major news organizations, peer-reviewed journals). Use markdown link formatting:

---
`);
    };

    /**
     * computeFileMD5 — hashes the raw file bytes using SparkMD5.
     * The hash becomes part of the GCS object name to deduplicate uploads:
     * the same file uploaded twice resolves to the same object key.
     *
     * @param {File|UploadFile} file — Ant Design UploadFile or native File.
     * @returns {Promise<string>} hex MD5 digest.
     */
    async function computeFileMD5(file) {
        const nativeFile = file.originFileObj || file;
        if (!nativeFile) throw new Error("No file to hash");
        const arrayBuffer = await nativeFile.arrayBuffer();
        return SparkMD5.ArrayBuffer.hash(arrayBuffer);
    }

    /**
     * getVideoThumbnailCanvas — extracts a video frame at t=1s and draws it
     * onto a canvas at 1/4 of the original resolution.
     * The canvas is later converted to a PNG thumbnail via canvasToFile().
     *
     * @param {File|UploadFile} file — video file to extract thumbnail from.
     * @returns {Promise<HTMLCanvasElement>}
     */
    async function getVideoThumbnailCanvas(file) {
        return new Promise((resolve, reject) => {
            const nativeFile = file.originFileObj || file;
            const video = document.createElement("video");
            video.src = URL.createObjectURL(nativeFile);
            video.currentTime = 1;  // seek to 1 second for a representative frame
            video.muted = true;
            video.addEventListener("loadeddata", () => {
                const canvas = document.createElement("canvas");
                // Scale down to 1/4 to keep thumbnail size small
                canvas.width = video.videoWidth / 4;
                canvas.height = video.videoHeight / 4;
                canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
                resolve(canvas);
            });
            video.addEventListener("error", reject);
        });
    }

    /**
     * canvasToFile — converts an HTMLCanvasElement to a PNG File object.
     * Used to package the video thumbnail before uploading to GCS.
     *
     * @param {HTMLCanvasElement} canvas
     * @param {string}            filename — default "thumb.png"
     * @param {string}            type     — MIME type, default "image/png"
     * @returns {Promise<File>}
     */
    function canvasToFile(canvas, filename = "thumb.png", type = "image/png") {
        return new Promise((resolve, reject) => {
            canvas.toBlob(blob => {
                if (!blob) return reject("Failed to create blob from canvas");
                resolve(new File([blob], filename, { type }));
            }, type);
        });
    }

    /**
     * handleUpload — custom upload handler for Ant Design's Upload component.
     *
     * Upload flow:
     *   1. Hash the file with SparkMD5 → deterministic GCS object name.
     *   2. Request a pre-signed PUT URL from the backend via apiClient.sessionurl().
     *   3. PUT the file directly to GCS (backend is not in the data path).
     *   4. For videos: also extract, hash-name, and upload a thumbnail PNG.
     *   5. Append the completed { uid, name, url, thumbUrl } entry to fileList.
     *
     * Returns Upload.LIST_IGNORE to prevent Ant Design from adding the raw
     * file object to its internal list (we manage fileList state ourselves).
     *
     * @param {object} file — Ant Design customRequest parameter ({ file }).
     */
    const handleUpload = async (file) => {
        const fileObj = file.file;
        setFileList(prev => [...prev, { uid: fileObj.uid, name: fileObj.name, status: "uploading", percent: 0 }]);
        try {
            if (fileObj.type.startsWith("video/")) {
                // Video branch: upload both the video and a thumbnail
                const fileHash = await computeFileMD5(fileObj);
                const ext = fileObj.name.split(".").pop();
                const objectName = "videos/" + fileHash + "." + ext;
                const thumbCanvas = await getVideoThumbnailCanvas(fileObj);
                const thumbFile = await canvasToFile(thumbCanvas, fileObj.name + "-thumb.png");
                const res = await apiClient.sessionurl(objectName, fileObj.type);
                const data = await res.data;
                // Video upload tracked to 85%; thumbnail upload covers 85→100%
                await xhrUpload(data.uploadUrl, fileObj, fileObj.type, pct =>
                    setFileList(prev => prev.map(f => f.uid === fileObj.uid ? { ...f, percent: Math.round(pct * 0.85) } : f))
                );
                setFileList(prev => prev.map(f => f.uid === fileObj.uid ? { ...f, percent: 92 } : f));
                await fetch(data.uploadThumbUrl, { method: "PUT", headers: { "Content-Type": thumbFile.type }, body: thumbFile });
                setFileList(prev => prev.map(f => f.uid === fileObj.uid
                    ? { uid: fileObj.uid, name: fileObj.name, url: data.publicUrl, thumbUrl: data.publicThumbUrl, status: "done", percent: 100 }
                    : f
                ));
            } else {
                // Image branch: upload the image only
                const fileHash = await computeFileMD5(fileObj);
                const ext = fileObj.name.split(".").pop();
                const objectName = "images/" + fileHash + "." + ext;
                const res = await apiClient.sessionurl(objectName, fileObj.type);
                const data = await res.data;
                await xhrUpload(data.uploadUrl, fileObj, fileObj.type, pct =>
                    setFileList(prev => prev.map(f => f.uid === fileObj.uid ? { ...f, percent: pct } : f))
                );
                setFileList(prev => prev.map(f => f.uid === fileObj.uid
                    ? { uid: fileObj.uid, name: fileObj.name, url: data.publicUrl, thumbUrl: undefined, status: "done", percent: 100 }
                    : f
                ));
            }
        } catch (error) {
            console.error("Upload failed:", error);
            setFileList(prev => prev.map(f => f.uid === fileObj.uid ? { ...f, status: "error" } : f));
        }
        return Upload.LIST_IGNORE;
    };

    /**
     * handlePreview — copies a markdown-formatted image or video link to
     * the clipboard so the FACT_CHECKER can paste it into the explanation.
     *
     * Image:  ![name](url)
     * Video:  [![name](thumbUrl)](url)   — linked thumbnail for click-to-play
     *
     * @param {object} file — Ant Design UploadFile entry (has url + thumbUrl).
     */
    const handlePreview = async (file) => {
        const text = file.thumbUrl !== undefined
            ? "[![" + file.name + "](" + file.thumbUrl + ")](" + file.url + ")"
            : "[![" + file.name + "](" + file.url + ")](" + file.url + ")";
        navigator.clipboard.writeText(text);
    };

    /**
     * handleRemove — queues a file for deletion rather than deleting immediately.
     * The entry is removed from fileList (hidden from the upload grid) and added
     * to deleteList.  The actual GCS deletion fires on OK via apiClient.removeurl().
     * This avoids permanently deleting a file if the user cancels the drawer.
     *
     * @param {object} file — Ant Design UploadFile entry being removed.
     */
    const handleRemove = async (file) => {
        if (file.url) {
            const entry = "thumbUrl" in file
                ? { url: file.url, thumbUrl: file.thumbUrl }
                : { url: file.url, thumbUrl: null };
            setDeleteList(prev => [...prev, entry]);
        }
        setFileList(prev => prev.filter(item => item.uid !== file.uid));
        return Upload.LIST_IGNORE;
    };

    /**
     * handleOk — dirty check before saving.
     *
     * Only calls onSubmit (→ apiClient.updatePost) if at least one of the
     * three editable fields (lbl / exp / files) has actually changed.
     * This prevents unnecessary backend writes when a FACT_CHECKER opens
     * a post and clicks OK without making any edits.
     *
     * If changes are detected:
     *   - Mutates inpPost in-place so the parent component reflects the new values.
     *   - Fires the batched GCS deletion for any removed files.
     *   - Calls onSubmit() to persist changes to the backend.
     *
     * If no changes: calls onClose() to dismiss without any side effects.
     */
    const handleOk = () => {
        if (usr.role === "FACT_CHECKER") {
            if ((inpPost.lbl !== lbl) || (inpPost.exp !== exp) || (inpPost.files !== fileList)) {
                inpPost.lbl = lbl;
                inpPost.exp = exp;
                if (deleteList.length > 0) apiClient.removeurl(deleteList);
                inpPost.files = fileList;
                onSubmit();
            } else {
                if (onAck) onAck(); else onClose();
            }
        } else {
            if (onAck) onAck(); else onClose();
        }
    };

    // canEdit gates the entire edit UI — FC role AND post not yet released
    const canEdit = usr.role === "FACT_CHECKER" && !inpPost.released;

    return (
        <div>
            {messageHolder}
            <Drawer
                placement="bottom"
                height="92vh"
                open={isOpen}
                onClose={onClose}
                title={null}
                closable={false}
                styles={{
                    body: { padding: 0, display: "flex", flexDirection: "column", height: "100%" },
                    wrapper: { borderRadius: "20px 20px 0 0", overflow: "hidden" },
                }}
            >
                {/* Drag handle pill — visual affordance matching Flutter's DraggableScrollableSheet */}
                <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 8px", flexShrink: 0 }}>
                    <div style={{ width: 40, height: 4, borderRadius: 2, background: "#d9d9d9" }} />
                </div>

                {/* Header: title (flex:1) | reactions centred | verdict control (flex:1 right-aligned) */}
                {/* Equal-width 52px units keep the trio symmetric so 🚩 sits at the geometric centre */}
                <div style={{ display: "flex", alignItems: "center", padding: "0 16px 12px", flexShrink: 0 }}>
                    <div style={{ flex: 1 }}><Text strong style={{ fontSize: 16 }}>Verdict</Text></div>
                    {usr.role === "FACT_CHECKER" && (
                        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                            {inpPost.lbl !== "BLANK" && (
                                <span style={{ width: 52, display: "inline-flex", justifyContent: "center", alignItems: "center", gap: 3, fontSize: 13, color: "#52c41a", cursor: "pointer", userSelect: "none" }} onClick={() => tapAffinity(1)}>
                                    <span>👍</span><span>{_userAffinity === 1 ? `${_likes - 1}+1` : _likes}</span>
                                </span>
                            )}
                            <span style={{ width: 52, display: "inline-flex", justifyContent: "center", alignItems: "center", gap: 3, fontSize: 13, color: "#1677ff", cursor: "pointer", userSelect: "none" }} onClick={tapSeverity}>
                                <span>🚩</span><span>{_userSeverity === 1 ? `${_severityCount - 1}+1` : _severityCount}</span>
                            </span>
                            {inpPost.lbl !== "BLANK" && (
                                <span style={{ width: 52, display: "inline-flex", justifyContent: "center", alignItems: "center", gap: 3, fontSize: 13, color: "#ff4d4f", cursor: "pointer", userSelect: "none" }} onClick={() => tapAffinity(-1)}>
                                    <span>👎</span><span>{_userAffinity === -1 ? `${_dislikes - 1}+1` : _dislikes}</span>
                                </span>
                            )}
                        </div>
                    )}
                    <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                        {canEdit ? (
                            /* FACT_CHECKER: editable verdict selector */
                            <Select
                                className="verdict-select"
                                style={{ width: 120 }}
                                defaultValue={lbl}
                                options={lbls}
                                onChange={e => setLbl(e)}
                                dropdownStyle={{ textAlign: "center" }}
                            />
                        ) : (
                            /* Read-only: coloured verdict badge */
                            <Tag color={LBL_COLOR[lbl] || "default"}>{lbl}</Tag>
                        )}
                    </div>
                </div>
                <Divider style={{ margin: 0, flexShrink: 0 }} />

                {/* Scrollable body */}
                <div style={{ flex: 1, overflowY: "auto", padding: "8px 16px 16px" }}>
                    {/* Markdown 1: ### Claim + message + optional ### Attachments heading */}
                    <div className="markdown-container">
                        <Markdown>{`### 🧾 Claim\n${inpPost.msg}${inpPost.claim_files?.length > 0 ? '\n\n### 🔍 Attachments' : ''}`}</Markdown>
                    </div>

                    {/* Claim attachments — read-only picture-card grid.
                        Preview icon copies the markdown link to clipboard so the
                        fact-checker can paste it into the explanation textarea. */}
                    {inpPost.claim_files?.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                            <Upload
                                listType="picture-card"
                                fileList={inpPost.claim_files.map(f => ({ ...f, status: "done" }))}
                                showUploadList={{
                                    showPreviewIcon: true,
                                    showRemoveIcon: false,
                                    showDownloadIcon: true,
                                    downloadIcon: <ExportOutlined style={{ color: 'white' }} />,
                                }}
                                onPreview={(file) => {
                                    const text = file.thumbUrl
                                        ? "[![" + file.name + "](" + file.thumbUrl + ")](" + file.url + ")"
                                        : "[![" + file.name + "](" + file.url + ")](" + file.url + ")";
                                    navigator.clipboard.writeText(text);
                                }}
                                onDownload={(file) => window.open(file.url, '_blank')}
                            />
                        </div>
                    )}

                    <Divider style={{ margin: "4px 0" }}><em style={{ fontWeight: "normal", color: "rgba(0, 0, 0, 0.25)" }}>FACT</em></Divider>

                    {canEdit ? (
                        /* -------------------------------------------------------
                           FACT_CHECKER edit section
                           Single row: attachments (left) + controls (right)
                           Media: Upload panel (max 4 files, compact 60px cells)
                           Markdown 3: TextArea editor / rendered preview
                        ------------------------------------------------------- */
                        <div>
                            {/* Verdict attachments (left) + controls (right) — one row */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 8, marginBottom: 8 }}>
                                <div className="compact-upload">
                                    <Upload
                                        listType="picture-card"
                                        fileList={fileList}
                                        showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
                                        onPreview={handlePreview}
                                        customRequest={handleUpload}
                                        onRemove={handleRemove}
                                    >
                                        {fileList.length >= 4 ? null : (
                                            <button style={{ border: 0, background: "none" }} type="button">
                                                <PlusOutlined />
                                                <div style={{ marginTop: 4, fontSize: 11 }}>Upload</div>
                                            </button>
                                        )}
                                    </Upload>
                                </div>
                                <Space size={8} style={{ flexShrink: 0 }}>
                                    {/* AI fill — calls Gemini via the backend fact_check endpoint */}
                                    {false && <Button
                                        style={{ padding: 0, opacity: isAiLoading ? 0.5 : 1 }}
                                        onClick={gen_ai_fill}
                                        title="gemini-generative-fill"
                                        type="link"
                                        size="large"
                                        disabled={isAiLoading}
                                    >
                                        <img src="gemini.png" alt="gemini-generative-fill" style={{ width: 30, height: 30 }} />
                                    </Button>}
                                    {/* Preview/Markdown toggle */}
                                    <Radio.Group value={view} onChange={e => setView(e.target.value)}>
                                        <Radio.Button value="Preview">Preview</Radio.Button>
                                        <Radio.Button value="Markdown">Markdown</Radio.Button>
                                    </Radio.Group>
                                    {/* Template button */}
                                    <Button icon={<FormOutlined />} onClick={exp_fill_template} />
                                </Space>
                            </div>

                            {/* Markdown 3: Toggle between raw markdown editor and rendered preview */}
                            {view === "Markdown" ? (
                                <div className={isAiLoading ? "gemini-loading-wrapper" : ""} style={{ marginBottom: 12, marginTop: 12 }}>
                                    <TextArea
                                        value={exp}
                                        showCount
                                        maxLength={10000}
                                        onChange={e => setExp(e.target.value)}
                                        placeholder="Enter justifications…"
                                        autoSize={{ minRows: 20 }}
                                    />
                                </div>
                            ) : (
                                <div className="markdown-container" style={{
                                    border: "1px solid #e8e8e8",
                                    borderRadius: 6,
                                    padding: 16,
                                    marginTop: 12,
                                    marginBottom: 12,
                                }}>
                                    <Markdown>{exp}</Markdown>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* Read-only fact section */
                        <div>
                            <div className="markdown-container" style={{ marginTop: 8 }}>
                                <Markdown>{inpPost.exp}</Markdown>
                            </div>
                        </div>
                    )}

                </div>

                {/* Footer — attribution left, action button right.
                    Marquee activates only when the text overflows the available width. */}
                <div style={{ padding: "8px 16px 16px", borderTop: "1px solid #f0f0f0", flexShrink: 0, display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ flex: 1, overflow: "hidden" }}>
                        {needsMarquee ? (
                            <Marquee pauseOnHover gradient={false} speed={30}>
                                <Text type="secondary" style={{ fontSize: 13, paddingRight: 48 }}>
                                    Fact Checked by: <Text strong>{inpPost.by}</Text>
                                    {inpPost.approvers?.length > 0 && (
                                        <> &nbsp;|&nbsp; Approved by: <Text strong>{inpPost.approvers.join(', ')}</Text></>
                                    )}
                                </Text>
                            </Marquee>
                        ) : (
                            <span ref={attrRef} style={{ whiteSpace: 'nowrap', display: 'block' }}>
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                    Fact Checked by: <Text strong>{inpPost.by}</Text>
                                    {inpPost.approvers?.length > 0 && (
                                        <> &nbsp;|&nbsp; Approved by: <Text strong>{inpPost.approvers.join(', ')}</Text></>
                                    )}
                                </Text>
                            </span>
                        )}
                    </div>
                    <Button type="primary" onClick={handleOk} style={{ flexShrink: 0 }}>
                        {canEdit ? "OK" : "Got it"}
                    </Button>
                </div>
            </Drawer>
        </div>
    );
};
